import { Add as AddIcon, ArrowDownward, ArrowUpward, DeleteOutline } from '@mui/icons-material';
import {
    Box,
    Button,
    IconButton,
    MenuItem,
    Paper,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { JSX, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    CatalogDraft,
    DirectionDraft,
    OptionDraft,
    SectionDraft,
    TypeDraft,
    emptyDirection,
    emptyOption,
    emptySection,
    emptyType,
    isCountable,
    isLinkUsable,
    SessionChoice,
    sessionChoices,
} from '../../../library/models/serviceCatalog';
import ResourcesIds from '../../../resources/ResourcesIds';
import { ConfirmationDialog } from '../../confirmationDialog/confirmationDialog';
import {
    addButtonStyles,
    addDirectionButtonStyles,
    directionCardStyles,
    editorWrapperStyles,
    emptyEditorStyles,
    nodeFieldsStyles,
    nodeHeaderStyles,
    nodeLabelStyles,
    basedOnFieldStyles,
    kindToggleStyles,
    optionNumberFieldStyles,
    optionRowStyles,
    priceFieldStyles,
    rowActionsStyles,
    sectionCardStyles,
    textFieldStyles,
    typeCardStyles,
} from './ourServicesEditor.styles';

interface IOurServicesEditorProps {
    draft: CatalogDraft;
    /**
     * Node keys the API would reject. Passed in rather than computed here so the page can
     * use the same answer to decide whether Save is available -- one rule, one evaluation.
     */
    invalidKeys: Set<string>;
    onChange: (draft: CatalogDraft) => void;
}

/**
 * Which container a pending deletion is about. Only directions and sections are confirmed:
 * they take their children down with them, whereas a service or a price line is one row
 * the admin can retype.
 */
interface PendingDelete {
    directionIndex: number;
    sectionIndex?: number;
}

const replaceAt = <T,>(list: T[], index: number, value: T): T[] =>
    list.map((item, position) => (position === index ? value : item));

const removeAt = <T,>(list: T[], index: number): T[] =>
    list.filter((_, position) => position !== index);

/** Returns the list unchanged when the move would fall off either end. */
const moveBy = <T,>(list: T[], index: number, offset: number): T[] => {
    const target: number = index + offset;

    if (target < 0 || target >= list.length) {
        return list;
    }

    const moved: T[] = [...list];
    [moved[index], moved[target]] = [moved[target], moved[index]];

    return moved;
};

/**
 * What flipping the plan/session toggle changes.
 *
 * The session count *is* the kind -- one is a session, more is a plan -- so the toggle
 * writes that count rather than a second field beside it that could disagree with it.
 *
 * Switching to a plan leaves a count that says so, in the spirit of the sixty minutes a new
 * line starts with: a starting point to overtype rather than an empty box with an error
 * under it. Switching back drops the link, which would otherwise sit there naming a session
 * this line is no longer a block of.
 */
const asKind = (kind: string, option: OptionDraft): Partial<OptionDraft> =>
    (kind === 'plan'
        ? { sessionCount: Number(option.sessionCount) > 1 ? option.sessionCount : '5' }
        : { sessionCount: '1', basedOnKey: '' });

/**
 * The whole price list as a form.
 *
 * Holds no copy of the draft: every keystroke goes up to the page, which owns it and the
 * single Save that sends it. Order is position in the list, so the arrows are the only
 * reordering there is -- there is nothing else for the admin to set.
 */
export const OurServicesEditor = (props: IOurServicesEditorProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const [pendingDelete, setPendingDelete] = useState<PendingDelete | null>(null);

    const directions: DirectionDraft[] = props.draft.directions;

    /*
     * What a plan can be a block of, and the same answer as a set for the rows that only
     * need to ask whether a link still points at something.
     *
     * Recomputed on every keystroke rather than memoised, deliberately: the labels are the
     * admin's own text, so a discipline being renamed has to show up in the pickers as it
     * is typed. The list is a handful of entries per clinic.
     */
    const choices: SessionChoice[] = sessionChoices(props.draft);
    const sessionKeys: Set<string> = new Set(choices.map(choice => choice.key));

    const setDirections = (next: DirectionDraft[]): void => props.onChange({ directions: next });

    const updateDirection = (index: number, changes: Partial<DirectionDraft>): void =>
        setDirections(replaceAt(directions, index, { ...directions[index], ...changes }));

    const updateSections = (directionIndex: number, next: SectionDraft[]): void =>
        updateDirection(directionIndex, { sections: next });

    const updateSection = (directionIndex: number, sectionIndex: number,
        changes: Partial<SectionDraft>): void => {
        const sections: SectionDraft[] = directions[directionIndex].sections;
        updateSections(directionIndex,
            replaceAt(sections, sectionIndex, { ...sections[sectionIndex], ...changes }));
    };

    const updateTypes = (directionIndex: number, sectionIndex: number, next: TypeDraft[]): void =>
        updateSection(directionIndex, sectionIndex, { types: next });

    const updateType = (directionIndex: number, sectionIndex: number, typeIndex: number,
        changes: Partial<TypeDraft>): void => {
        const types: TypeDraft[] = directions[directionIndex].sections[sectionIndex].types;
        updateTypes(directionIndex, sectionIndex,
            replaceAt(types, typeIndex, { ...types[typeIndex], ...changes }));
    };

    const updateOptions = (directionIndex: number, sectionIndex: number, typeIndex: number,
        next: OptionDraft[]): void =>
        updateType(directionIndex, sectionIndex, typeIndex, { options: next });

    const updateOption = (directionIndex: number, sectionIndex: number, typeIndex: number,
        optionIndex: number, changes: Partial<OptionDraft>): void => {
        const options: OptionDraft[] =
            directions[directionIndex].sections[sectionIndex].types[typeIndex].options;
        updateOptions(directionIndex, sectionIndex, typeIndex,
            replaceAt(options, optionIndex, { ...options[optionIndex], ...changes }));
    };

    const confirmDelete = (): void => {
        if (!pendingDelete) {
            return;
        }

        const { directionIndex, sectionIndex } = pendingDelete;
        setPendingDelete(null);

        if (sectionIndex === undefined) {
            setDirections(removeAt(directions, directionIndex));
            return;
        }

        updateSections(directionIndex, removeAt(directions[directionIndex].sections, sectionIndex));
    };

    /** The up/down/delete cluster every row carries, differing only in what delete does. */
    const rowActions = (
        onMoveUp: () => void,
        onMoveDown: () => void,
        onDelete: () => void,
        deleteLabel: string,
        isFirst: boolean,
        isLast: boolean
    ): JSX.Element => (
        <Box sx={rowActionsStyles}>
            <Tooltip title={translate(ResourcesIds.SERVICES_MOVE_UP)}>
                <span>
                    <IconButton
                        size="small"
                        disabled={isFirst}
                        onClick={onMoveUp}
                        aria-label={translate(ResourcesIds.SERVICES_MOVE_UP)}
                    >
                        <ArrowUpward fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={translate(ResourcesIds.SERVICES_MOVE_DOWN)}>
                <span>
                    <IconButton
                        size="small"
                        disabled={isLast}
                        onClick={onMoveDown}
                        aria-label={translate(ResourcesIds.SERVICES_MOVE_DOWN)}
                    >
                        <ArrowDownward fontSize="small" />
                    </IconButton>
                </span>
            </Tooltip>

            <Tooltip title={translate(deleteLabel)}>
                <IconButton
                    size="small"
                    color="error"
                    onClick={onDelete}
                    aria-label={translate(deleteLabel)}
                >
                    <DeleteOutline fontSize="small" />
                </IconButton>
            </Tooltip>
        </Box>
    );

    /**
     * The Romanian/English pair every named node has. Only the Romanian side can be wrong:
     * English is optional and falls back to it.
     */
    const languageFields = (
        nodeKey: string,
        valueRo: string,
        valueEn: string,
        onChangeRo: (value: string) => void,
        onChangeEn: (value: string) => void
    ): JSX.Element => {
        const isInvalid: boolean = props.invalidKeys.has(nodeKey);

        return (
            <Box sx={nodeFieldsStyles}>
                <TextField
                    size="small"
                    sx={textFieldStyles}
                    label={translate(ResourcesIds.SERVICES_FIELD_RO)}
                    value={valueRo}
                    onChange={event => onChangeRo(event.target.value)}
                    error={isInvalid}
                    helperText={isInvalid ? translate(ResourcesIds.SERVICES_REQUIRED_ERROR) : ' '}
                />

                <TextField
                    size="small"
                    sx={textFieldStyles}
                    label={translate(ResourcesIds.SERVICES_FIELD_EN)}
                    value={valueEn}
                    onChange={event => onChangeEn(event.target.value)}
                    helperText=" "
                />
            </Box>
        );
    };

    return (
        <Box sx={editorWrapperStyles}>
            {directions.length === 0 && (
                <Typography variant="body2" sx={emptyEditorStyles}>
                    {translate(ResourcesIds.SERVICES_EMPTY_EDITOR)}
                </Typography>
            )}

            {directions.map((direction, directionIndex) => (
                <Paper key={direction.key} elevation={0} sx={directionCardStyles}>
                    <Box sx={nodeHeaderStyles}>
                        <Typography sx={nodeLabelStyles}>
                            {translate(ResourcesIds.SERVICES_DIRECTION_LABEL)}
                        </Typography>

                        {languageFields(
                            direction.key,
                            direction.titleRo,
                            direction.titleEn,
                            value => updateDirection(directionIndex, { titleRo: value }),
                            value => updateDirection(directionIndex, { titleEn: value })
                        )}

                        {rowActions(
                            () => setDirections(moveBy(directions, directionIndex, -1)),
                            () => setDirections(moveBy(directions, directionIndex, 1)),
                            () => setPendingDelete({ directionIndex }),
                            ResourcesIds.SERVICES_DELETE_DIRECTION,
                            directionIndex === 0,
                            directionIndex === directions.length - 1
                        )}
                    </Box>

                    {direction.sections.map((section, sectionIndex) => (
                        <Paper key={section.key} elevation={0} sx={sectionCardStyles}>
                            <Box sx={nodeHeaderStyles}>
                                <Typography sx={nodeLabelStyles}>
                                    {translate(ResourcesIds.SERVICES_SECTION_LABEL)}
                                </Typography>

                                {languageFields(
                                    section.key,
                                    section.titleRo,
                                    section.titleEn,
                                    value => updateSection(directionIndex, sectionIndex, { titleRo: value }),
                                    value => updateSection(directionIndex, sectionIndex, { titleEn: value })
                                )}

                                {rowActions(
                                    () => updateSections(directionIndex,
                                        moveBy(direction.sections, sectionIndex, -1)),
                                    () => updateSections(directionIndex,
                                        moveBy(direction.sections, sectionIndex, 1)),
                                    () => setPendingDelete({ directionIndex, sectionIndex }),
                                    ResourcesIds.SERVICES_DELETE_SECTION,
                                    sectionIndex === 0,
                                    sectionIndex === direction.sections.length - 1
                                )}
                            </Box>

                            {section.types.map((type, typeIndex) => (
                                <Paper key={type.key} elevation={0} sx={typeCardStyles}>
                                    <Box sx={nodeHeaderStyles}>
                                        <Typography sx={nodeLabelStyles}>
                                            {translate(ResourcesIds.SERVICES_TYPE_LABEL)}
                                        </Typography>

                                        {languageFields(
                                            type.key,
                                            type.nameRo,
                                            type.nameEn,
                                            value => updateType(directionIndex, sectionIndex, typeIndex,
                                                { nameRo: value }),
                                            value => updateType(directionIndex, sectionIndex, typeIndex,
                                                { nameEn: value })
                                        )}

                                        {rowActions(
                                            () => updateTypes(directionIndex, sectionIndex,
                                                moveBy(section.types, typeIndex, -1)),
                                            () => updateTypes(directionIndex, sectionIndex,
                                                moveBy(section.types, typeIndex, 1)),
                                            () => updateTypes(directionIndex, sectionIndex,
                                                removeAt(section.types, typeIndex)),
                                            ResourcesIds.SERVICES_DELETE_TYPE,
                                            typeIndex === 0,
                                            typeIndex === section.types.length - 1
                                        )}
                                    </Box>

                                    {type.options.map((option, optionIndex) => {
                                        const isInvalid: boolean = props.invalidKeys.has(option.key);
                                        const isPriceInvalid: boolean = isInvalid
                                            && !(Number(option.price) >= 0 && option.price.trim().length > 0);
                                        // The row is flagged as a whole, so each box works
                                        // out for itself whether it is the one at fault --
                                        // otherwise a bad price would redden the minutes.
                                        const isMinutesInvalid: boolean = isInvalid
                                            && !isCountable(option.durationMinutes);
                                        const isSessionsInvalid: boolean = isInvalid
                                            && !isCountable(option.sessionCount);

                                        // A plan sells more than one session; that count is
                                        // the same thing the toggle says, so there is one
                                        // fact here rather than two that could disagree.
                                        const isPlan: boolean = Number(option.sessionCount) > 1;
                                        const isBasedOnInvalid: boolean = isInvalid
                                            && !isLinkUsable(option, sessionKeys);

                                        return (
                                            <Box key={option.key} sx={optionRowStyles}>
                                                <Typography sx={nodeLabelStyles}>
                                                    {translate(ResourcesIds.SERVICES_OPTION_LABEL)}
                                                </Typography>

                                                <TextField
                                                    size="small"
                                                    sx={textFieldStyles}
                                                    label={translate(ResourcesIds.SERVICES_FIELD_RO)}
                                                    value={option.durationRo}
                                                    onChange={event => updateOption(directionIndex, sectionIndex,
                                                        typeIndex, optionIndex,
                                                        { durationRo: event.target.value })}
                                                    error={isInvalid && option.durationRo.trim().length === 0}
                                                    helperText={isInvalid && option.durationRo.trim().length === 0
                                                        ? translate(ResourcesIds.SERVICES_REQUIRED_ERROR)
                                                        : ' '}
                                                />

                                                <TextField
                                                    size="small"
                                                    sx={textFieldStyles}
                                                    label={translate(ResourcesIds.SERVICES_FIELD_EN)}
                                                    value={option.durationEn}
                                                    onChange={event => updateOption(directionIndex, sectionIndex,
                                                        typeIndex, optionIndex,
                                                        { durationEn: event.target.value })}
                                                    helperText=" "
                                                />

                                                {/* What this line is. A group with nothing
                                                    selected is what a second click on the
                                                    active button produces; keep the answer
                                                    instead. */}
                                                <ToggleButtonGroup
                                                    exclusive
                                                    size="small"
                                                    sx={kindToggleStyles}
                                                    value={isPlan ? 'plan' : 'session'}
                                                    onChange={(_, next: string | null) =>
                                                        next && updateOption(directionIndex, sectionIndex,
                                                            typeIndex, optionIndex, asKind(next, option))}
                                                >
                                                    <ToggleButton value="session">
                                                        {translate(ResourcesIds.SERVICES_KIND_SESSION)}
                                                    </ToggleButton>
                                                    <ToggleButton value="plan">
                                                        {translate(ResourcesIds.SERVICES_KIND_PLAN)}
                                                    </ToggleButton>
                                                </ToggleButtonGroup>

                                                {/* A session says how long it runs. A plan
                                                    takes that from the session it is a block
                                                    of, so asking again could only produce a
                                                    second answer to one question. */}
                                                {!isPlan && (
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        sx={optionNumberFieldStyles}
                                                        label={translate(ResourcesIds.SERVICES_FIELD_MINUTES)}
                                                        value={option.durationMinutes}
                                                        onChange={event => updateOption(directionIndex,
                                                            sectionIndex, typeIndex, optionIndex,
                                                            { durationMinutes: event.target.value })}
                                                        error={isMinutesInvalid}
                                                        helperText={isMinutesInvalid
                                                            ? translate(ResourcesIds.SERVICES_COUNT_ERROR)
                                                            : ' '}
                                                    />
                                                )}

                                                {isPlan && (
                                                    <>
                                                        {/* Every session line in the whole
                                                            catalogue, since plans live in a
                                                            section of their own and the
                                                            session is almost never a
                                                            sibling. */}
                                                        <TextField
                                                            select
                                                            size="small"
                                                            sx={basedOnFieldStyles}
                                                            label={translate(
                                                                ResourcesIds.SERVICES_FIELD_BASED_ON)}
                                                            value={option.basedOnKey}
                                                            onChange={event => updateOption(directionIndex,
                                                                sectionIndex, typeIndex, optionIndex,
                                                                { basedOnKey: event.target.value })}
                                                            error={isBasedOnInvalid}
                                                            helperText={isBasedOnInvalid
                                                                ? translate(
                                                                    ResourcesIds.SERVICES_BASED_ON_ERROR)
                                                                : translate(
                                                                    ResourcesIds.SERVICES_BASED_ON_HINT)}
                                                        >
                                                            {choices.map((choice: SessionChoice) => (
                                                                <MenuItem key={choice.key} value={choice.key}>
                                                                    {choice.label}
                                                                </MenuItem>
                                                            ))}
                                                        </TextField>

                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            sx={optionNumberFieldStyles}
                                                            label={translate(
                                                                ResourcesIds.SERVICES_FIELD_SESSIONS)}
                                                            value={option.sessionCount}
                                                            onChange={event => updateOption(directionIndex,
                                                                sectionIndex, typeIndex, optionIndex,
                                                                { sessionCount: event.target.value })}
                                                            error={isSessionsInvalid}
                                                            helperText={isSessionsInvalid
                                                                ? translate(ResourcesIds.SERVICES_COUNT_ERROR)
                                                                : ' '}
                                                        />
                                                    </>
                                                )}

                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    sx={priceFieldStyles}
                                                    label={translate(isPlan
                                                        ? ResourcesIds.SERVICES_FIELD_PRICE_TOTAL
                                                        : ResourcesIds.SERVICES_FIELD_PRICE)}
                                                    value={option.price}
                                                    onChange={event => updateOption(directionIndex, sectionIndex,
                                                        typeIndex, optionIndex,
                                                        { price: event.target.value })}
                                                    error={isPriceInvalid}
                                                    helperText={isPriceInvalid
                                                        ? translate(ResourcesIds.SERVICES_PRICE_ERROR)
                                                        : ' '}
                                                />

                                                {rowActions(
                                                    () => updateOptions(directionIndex, sectionIndex, typeIndex,
                                                        moveBy(type.options, optionIndex, -1)),
                                                    () => updateOptions(directionIndex, sectionIndex, typeIndex,
                                                        moveBy(type.options, optionIndex, 1)),
                                                    () => updateOptions(directionIndex, sectionIndex, typeIndex,
                                                        removeAt(type.options, optionIndex)),
                                                    ResourcesIds.SERVICES_DELETE_OPTION,
                                                    optionIndex === 0,
                                                    optionIndex === type.options.length - 1
                                                )}
                                            </Box>
                                        );
                                    })}

                                    <Button
                                        size="small"
                                        startIcon={<AddIcon />}
                                        sx={addButtonStyles}
                                        onClick={() => updateOptions(directionIndex, sectionIndex, typeIndex,
                                            [...type.options, emptyOption()])}
                                    >
                                        {translate(ResourcesIds.SERVICES_ADD_OPTION)}
                                    </Button>
                                </Paper>
                            ))}

                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                sx={addButtonStyles}
                                onClick={() => updateTypes(directionIndex, sectionIndex,
                                    [...section.types, emptyType()])}
                            >
                                {translate(ResourcesIds.SERVICES_ADD_TYPE)}
                            </Button>
                        </Paper>
                    ))}

                    <Button
                        size="small"
                        startIcon={<AddIcon />}
                        sx={addButtonStyles}
                        onClick={() => updateSections(directionIndex,
                            [...direction.sections, emptySection()])}
                    >
                        {translate(ResourcesIds.SERVICES_ADD_SECTION)}
                    </Button>
                </Paper>
            ))}

            <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={addDirectionButtonStyles}
                onClick={() => setDirections([...directions, emptyDirection()])}
            >
                {translate(ResourcesIds.SERVICES_ADD_DIRECTION)}
            </Button>

            <ConfirmationDialog
                isOpen={pendingDelete !== null}
                onClose={() => setPendingDelete(null)}
                onConfirm={confirmDelete}
                title={translate(ResourcesIds.SERVICES_DELETE_TITLE)}
                description={translate(ResourcesIds.SERVICES_DELETE_DESCRIPTION)}
                confirmMessage={translate(pendingDelete?.sectionIndex === undefined
                    ? ResourcesIds.SERVICES_DELETE_DIRECTION
                    : ResourcesIds.SERVICES_DELETE_SECTION)}
                denyMessage={translate(ResourcesIds.CANCEL_TEXT)}
            />
        </Box>
    );
};
